import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'seller_token';

const iax = axios.create({ baseURL: API });
iax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export default function InventoryHistory({ onExit }) {
  const [branch, setBranch] = useState('all');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const q = branch === 'all' ? '' : `?branch=${branch}`;
      const { data } = await iax.get(`/public/inventory/history${q}`);
      setSessions(data.sessions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al cargar historial');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  const openDetail = async (id) => {
    setDetail({ loading: true, id });
    try {
      const { data } = await iax.get(`/public/inventory/history/${id}`);
      setDetail({ loading: false, ...data });
    } catch (e) {
      setDetail({ loading: false, error: 'Error al cargar detalle' });
    }
  };

  const downloadPdf = (id) => {
    const tok = localStorage.getItem(TOKEN_KEY);
    window.open(`${API}/public/inventory/history/${id}/pdf?token=${encodeURIComponent(tok || '')}`, '_blank');
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="inventory-history">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">📚 Historial de inventarios</h2>
          <p className="text-xs text-slate-500">Inventarios firmados y guardados por el administrador.</p>
        </div>
        <button onClick={onExit} className="text-xs bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded-full" data-testid="inv-hist-back">← Volver</button>
      </div>

      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-sm w-fit">
        {[
          { id: 'all', label: 'Todas' },
          { id: 'morelos', label: '🟢 Morelos' },
          { id: 'america', label: '🔵 América' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setBranch(t.id)}
            className={`px-4 py-1.5 rounded-md font-semibold ${branch === t.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
            data-testid={`inv-hist-branch-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>}
      {loading && <p className="text-center text-slate-500 py-8">Cargando…</p>}

      {!loading && sessions.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center text-slate-500">
          Aún no hay inventarios guardados.
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row gap-2 sm:items-center" data-testid={`inv-hist-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.branch_id.includes('f717') ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                    {s.branch_name}
                  </span>
                  {s.adjustments_applied ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Ajustes aplicados</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Solo registro</span>
                  )}
                  <span className="text-xs text-slate-500">
                    {new Date(s.created_at).toLocaleString('es-MX')}
                  </span>
                </div>
                <p className="text-sm mt-1"><b>{s.admin_name}</b></p>
                {s.reason && <p className="text-xs text-slate-500 truncate">Motivo: {s.reason}</p>}
                <div className="flex gap-3 text-xs text-slate-600 mt-1">
                  <span>Productos: <b>{s.totals?.productos ?? '-'}</b></span>
                  <span className="text-emerald-700">Correctos: <b>{s.totals?.correctos ?? '-'}</b></span>
                  <span className="text-rose-700">Faltantes: <b>{s.totals?.con_faltantes ?? '-'}</b></span>
                  <span className="text-amber-700">Sobrantes: <b>{s.totals?.con_sobrantes ?? '-'}</b></span>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => openDetail(s.id)} className="text-xs px-3 py-1.5 bg-slate-800 text-white rounded-lg" data-testid={`inv-hist-view-${s.id}`}>Ver</button>
                <button onClick={() => downloadPdf(s.id)} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg" data-testid={`inv-hist-pdf-${s.id}`}>PDF</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto">
            {detail.loading && <p className="text-center text-slate-500 py-6">Cargando…</p>}
            {!detail.loading && detail.error && <p className="text-rose-600">{detail.error}</p>}
            {!detail.loading && !detail.error && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Inventario {detail.branch_name}</h3>
                    <p className="text-xs text-slate-500">
                      {new Date(detail.created_at).toLocaleString('es-MX')} · Firma: <b>{detail.admin_name}</b>
                      {detail.adjustments_applied && <span className="text-amber-700"> · Ajustes aplicados</span>}
                    </p>
                    {detail.reason && <p className="text-xs text-slate-500">Motivo: {detail.reason}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => downloadPdf(detail.id)} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm">📄 PDF</button>
                    <button onClick={() => setDetail(null)} className="px-3 py-1.5 rounded-lg bg-slate-200 text-sm">Cerrar</button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div className="bg-slate-100 rounded p-2">Productos<br/><b className="text-lg">{detail.totals?.productos}</b></div>
                  <div className="bg-emerald-50 rounded p-2 text-emerald-700">Correctos<br/><b className="text-lg">{detail.totals?.correctos}</b></div>
                  <div className="bg-rose-50 rounded p-2 text-rose-700">Faltantes<br/><b className="text-lg">{detail.totals?.con_faltantes}</b></div>
                  <div className="bg-amber-50 rounded p-2 text-amber-700">Sobrantes<br/><b className="text-lg">{detail.totals?.con_sobrantes}</b></div>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
                    <tr>
                      <th className="p-1 text-left">Producto</th>
                      <th className="p-1 text-right">Esperado</th>
                      <th className="p-1 text-right">Contado</th>
                      <th className="p-1 text-right">Balance</th>
                      <th className="p-1 text-right">Costo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(detail.items || []).map(it => (
                      <tr key={it.product_id} className={it.status === 'faltan' ? 'bg-rose-50' : it.status === 'sobran' ? 'bg-amber-50' : ''}>
                        <td className="p-1">{it.name}</td>
                        <td className="p-1 text-right">{it.expected}</td>
                        <td className="p-1 text-right"><b>{it.counted}</b></td>
                        <td className={`p-1 text-right font-bold ${it.status === 'faltan' ? 'text-rose-700' : it.status === 'sobran' ? 'text-amber-700' : 'text-emerald-700'}`}>
                          {it.diff > 0 ? `+${it.diff}` : it.diff}
                        </td>
                        <td className="p-1 text-right text-slate-500">{it.purchase_cost != null ? `$${Number(it.purchase_cost).toFixed(2)}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
